import { useState } from 'react';
import { BellRing, CheckCircle2, ClipboardCheck, FileText, Video, VideoOff } from 'lucide-react';
import { Carte, Pastille } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { heureReunion, useGestesClasse, type DetailClasse } from '../../lib/useElearning';
import { urlPvSeance } from '../../lib/useEmargement';

type Participant = DetailClasse['participants'][number];

function initiales(nom: string): string {
  return nom
    .split(/\s+/)
    .map((partie) => partie[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const TEINTES: Record<string, string> = {
  success: 'text-codialis-teal',
  danger: 'text-red-600',
  warning: 'text-amber-700',
  neutral: 'text-codialis-gray',
};

/** Ce que la salle dit de l'apprenant, en une ligne : pendant le direct, l'état ; après, le relevé. */
function ligneSalle(participant: Participant, enDirect: boolean): { texte: string; teinte: string } {
  const salle = participant.salle;
  if (!enDirect) {
    return { texte: salle.resume, teinte: TEINTES[salle.etat === 'JAMAIS_CONNECTE' ? 'neutral' : 'success']! };
  }
  const teinte = TEINTES[salle.etatTon] ?? TEINTES['neutral']!;
  switch (salle.etat) {
    case 'CAMERA':
      return { texte: 'Caméra allumée · dans la salle', teinte };
    case 'SANS_CAMERA':
      return {
        texte: `Caméra coupée${salle.cameraCoupeeDepuis ? ` depuis ${salle.cameraCoupeeDepuis}` : ''} · à relancer`,
        teinte,
      };
    case 'HORS_SALLE':
      return { texte: `Sorti(e) de la salle${salle.sortie ? ` à ${salle.sortie}` : ''}`, teinte };
    default:
      return { texte: 'Pas encore connecté(e)', teinte };
  }
}

function pastilleEmargement(participant: Participant): { texte: string; ton: string } {
  if (participant.emarge) {
    return participant.statut === 'RETARD' ? { texte: 'Émargé · retard', ton: 'warning' } : { texte: 'Émargé', ton: 'success' };
  }
  if (participant.statut === 'ABSENT') return { texte: 'Absent', ton: 'danger' };
  return { texte: 'Non émargé', ton: 'neutral' };
}

/**
 * « Émargement numérique de la séquence » (CDC §6.7, maquette `form-elearning`).
 *
 * Chaque attendu, son émargement et ce que le relevé de la salle dit de lui :
 * pendant le direct, sa caméra — allumée, coupée depuis quand, hors de la
 * salle — et le geste « Relancer » ; ensuite, son temps de connexion, la part
 * caméra allumée et ses coupures. Le formateur y atteste la séance et clôture
 * la feuille une fois la diffusion fermée, comme sur la tablette.
 *
 * Tout vient de l'API, qui relit la salle sur le serveur de diffusion : la
 * carte ne déduit rien, elle compte seulement les états qu'elle reçoit.
 */
export function CarteEmargementClasse({ classe }: { classe: DetailClasse }): JSX.Element {
  const toast = useToast();
  const gestes = useGestesClasse();
  const [confirmation, setConfirmation] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const enDirect = classe.etat === 'EN_DIRECT';
  const cloturee = classe.feuille.clotureeLe !== null;
  const allumees = classe.participants.filter((participant) => participant.salle.etat === 'CAMERA').length;
  const coupees = classe.participants.filter((participant) => participant.salle.etat === 'SANS_CAMERA').length;

  const agir = async (geste: () => Promise<unknown>, succes: string, defaut: string): Promise<void> => {
    setErreur(null);
    try {
      await geste();
      toast(succes);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : defaut);
    }
  };

  const relancer = (participant: Participant) =>
    agir(
      () => gestes.relancer.mutateAsync({ seanceId: classe.seanceId, apprenantId: participant.apprenantId }),
      `Rappel envoyé à ${participant.nom} dans la salle`,
      'La relance n’a pas pu être envoyée.',
    );

  const cloturer = async () => {
    setErreur(null);
    try {
      const resultat = await gestes.cloturer.mutateAsync(classe.seanceId);
      toast(resultat.message);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'La feuille n’a pas pu être clôturée.');
    }
    setConfirmation(false);
  };

  return (
    <Carte
      titre="Émargement numérique de la séquence"
      actions={
        <>
          {enDirect && allumees > 0 && <Pastille texte={`${allumees} caméra${allumees > 1 ? 's' : ''} allumée${allumees > 1 ? 's' : ''}`} ton="success" />}
          {enDirect && coupees > 0 && <Pastille texte={`${coupees} coupée${coupees > 1 ? 's' : ''}`} ton="danger" />}
          <span className="text-xs text-codialis-gray">
            {classe.emarges}/{classe.attendus} émargé{classe.emarges > 1 ? 's' : ''}
          </span>
        </>
      }
    >
      {classe.participants.length === 0 ? (
        <p className="px-4 py-4 text-sm text-codialis-gray sm:px-6">Aucun apprenant en formation dans cette promotion.</p>
      ) : (
        <ul className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-6">
          {classe.participants.map((participant) => {
            const salle = ligneSalle(participant, enDirect);
            const pastille = pastilleEmargement(participant);
            const coupee = enDirect && participant.salle.etat === 'SANS_CAMERA';
            return (
              <li
                key={participant.apprenantId}
                className={`flex min-w-0 items-start gap-3 rounded-xl border px-3 py-2.5 ${
                  coupee ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-[10px] font-bold text-white">
                  {initiales(participant.nom)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-codialis-navy">{participant.nom}</div>
                  <div className={`flex items-start gap-1 text-[11px] ${salle.teinte}`}>
                    {enDirect &&
                      (participant.salle.etat === 'CAMERA' ? (
                        <Video className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                      ) : participant.salle.etat === 'SANS_CAMERA' ? (
                        <VideoOff className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                      ) : null)}
                    <span className="min-w-0">{salle.texte}</span>
                  </div>
                  <div className="text-[11px] text-codialis-gray">
                    {participant.emargeLe ? `Émargé à ${heureReunion(participant.emargeLe)}` : 'Pas encore émargé'}
                    {enDirect && participant.salle.coupuresCamera > 0
                      ? ` · ${participant.salle.coupuresCamera} coupure${participant.salle.coupuresCamera > 1 ? 's' : ''}`
                      : ''}
                    {participant.salle.relances > 0
                      ? ` · relancé${participant.salle.relances > 1 ? ` ${participant.salle.relances} fois` : ''}`
                      : ''}
                  </div>
                </div>
                {participant.refusRelance === null ? (
                  <button
                    type="button"
                    disabled={gestes.relancer.isPending}
                    onClick={() => void relancer(participant)}
                    className="inline-flex min-h-[36px] shrink-0 items-center gap-1.5 rounded-lg bg-red-600 px-2.5 text-xs font-bold text-white disabled:opacity-60 [@media(pointer:coarse)]:min-h-[40px]"
                  >
                    <BellRing className="h-3.5 w-3.5" aria-hidden />
                    Relancer
                  </button>
                ) : (
                  <span className="shrink-0">
                    <Pastille texte={pastille.texte} ton={pastille.ton} />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="bg-amber-50/80 px-4 py-3 text-xs text-amber-900 sm:px-6">
        Caméra obligatoire pendant les séquences en direct : l’apprenant n’émarge que caméra allumée, vérifiée par la
        plateforme dans la salle. Une coupure s’affiche ici et dans la salle du formateur ; elle est comptée au
        relevé de connexion du PV.
      </div>

      {classe.estAnimateur && (
        <div className="space-y-3 px-4 py-4 sm:px-6">
          {erreur && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
              {erreur}
            </p>
          )}
          {cloturee ? (
            <>
              <p className="flex items-start gap-2 rounded-xl bg-codialis-mint/10 p-3 text-xs text-codialis-navy">
                <CheckCircle2 className="mt-px h-4 w-4 shrink-0 text-codialis-teal" aria-hidden />
                Feuille clôturée à {heureReunion(classe.feuille.clotureeLe!)} · PV archivé avec le relevé de connexion.
                Les non-émargés ont été portés absents ; ils se régularisent sur la feuille d’émargement.
              </p>
              {classe.feuille.documentId && (
                <a
                  href={urlPvSeance(classe.seanceId)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
                >
                  <FileText className="h-4 w-4 shrink-0" aria-hidden />
                  PV de la classe
                </a>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {classe.feuille.formateurSigneLe ? (
                  <span className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-gray-50 px-4 text-sm text-codialis-navy">
                    <ClipboardCheck className="h-4 w-4 text-codialis-teal" aria-hidden />
                    Séance attestée à {heureReunion(classe.feuille.formateurSigneLe)}
                  </span>
                ) : (
                  classe.refus.attestation === null && (
                    <button
                      type="button"
                      disabled={gestes.attester.isPending}
                      onClick={() =>
                        void agir(
                          () => gestes.attester.mutateAsync(classe.seanceId),
                          'Séance attestée',
                          'La séance n’a pas pu être attestée.',
                        )
                      }
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy hover:bg-gray-50 disabled:opacity-60"
                    >
                      <ClipboardCheck className="h-4 w-4" aria-hidden />
                      Attester la séance
                    </button>
                  )
                )}
                {classe.refus.cloture === null && (
                  <button
                    type="button"
                    onClick={() => setConfirmation(true)}
                    className="inline-flex min-h-[44px] items-center rounded-xl bg-codialis-mint px-4 text-sm font-semibold text-codialis-navy transition hover:brightness-105"
                  >
                    Clôturer l’émargement
                  </button>
                )}
              </div>
              {classe.refus.cloture && classe.diffusion.ouverteLe && (
                <p className="text-[11px] text-codialis-gray">{classe.refus.cloture}</p>
              )}
            </>
          )}
        </div>
      )}

      <Modale
        titre="Clôturer l’émargement ?"
        sousTitre={`${classe.emarges} émargé${classe.emarges > 1 ? 's' : ''} sur ${classe.attendus}. Les non-émargés sont portés absents et le PV est archivé avec le relevé de connexion.`}
        ouverte={confirmation}
        onFermer={() => setConfirmation(false)}
        largeur="max-w-md"
      >
        <div className="flex flex-wrap justify-end gap-2 p-4 sm:p-6">
          <button
            type="button"
            onClick={() => setConfirmation(false)}
            className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={gestes.cloturer.isPending}
            onClick={() => void cloturer()}
            className="min-h-[44px] rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white disabled:opacity-60"
          >
            {gestes.cloturer.isPending ? 'Clôture…' : 'Clôturer'}
          </button>
        </div>
      </Modale>
    </Carte>
  );
}
